import { EyeOff, GripVertical, Save, X, Edit3, Settings, Home, Library, Trash2, Undo2 } from "lucide-react"
import React, { useCallback, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@/modern/components/ui/button";
import useViewsMenu from "@/react/hooks/useViewsMenu";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getViewStateIconByRecord } from "@/other/utils/utils.jsx";
import { StringHelper } from "@bryntum/schedulerpro";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure";
import { ThemeSwitcher } from "@/modern/components/theme-switcher";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";
import { getIsArchivedForBoard, saveIsArchivedForBoard } from "@/other/utils/otherUtils.jsx";
import { createAgendaUrl } from "@/env.jsx";
import { Checkbox } from "@/modern/components/ui/checkbox";

const PENDING_DELETE_TITLE = "This view will be permanently removed when you save changes";

function attachDragImage(e) {
  const dragElement = e.currentTarget.cloneNode(true);
  dragElement.style.position = "absolute";
  dragElement.style.top = "-1000px";
  dragElement.style.left = "-1000px";
  dragElement.style.width = `${e.currentTarget.offsetWidth}px`;
  dragElement.style.pointerEvents = "none";
  dragElement.style.opacity = "0.8";
  document.body.appendChild(dragElement);
  e.dataTransfer.setDragImage(dragElement, 0, 0);
  setTimeout(() => {
    if (document.body.contains(dragElement)) {
      document.body.removeChild(dragElement);
    }
  }, 0);
}

function PendingDeleteLabel({ className = "text-xs text-destructive/80 leading-4" }) {
  return (
    <span className={`${className} block truncate`} title={PENDING_DELETE_TITLE}>
      Removes on save
    </span>
  );
}

function EditableText({
  value,
  onChange,
  className = "",
  type = "text",
  editable = true,
  id,
  isEditMode,
  placeholder = "No description available.",
}) {
  if (!isEditMode || !editable) {
    return <span className={className}>{value}</span>
  }

  return (
    <input
      key={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} h-auto w-full max-w-full rounded-md border border-input bg-background px-2 py-1 shadow-sm placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-colors`}
      type={type}
      placeholder={placeholder}
    />
  )
}

function FloatingDeleteButton({ isPendingDelete, onClick }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={`absolute top-0 right-0 z-10 h-5 w-5 rounded-full p-0 shadow-sm transition-all ${
        isPendingDelete
          ? "bg-destructive text-destructive-foreground hover:!bg-destructive hover:!text-destructive-foreground hover:ring-1 hover:ring-destructive/40"
          : "bg-destructive/15 text-destructive hover:!bg-destructive/25 hover:!text-destructive"
      }`}
      onClick={onClick}
      title={isPendingDelete ? "Restore view" : "Delete view"}
    >
      {isPendingDelete ? (
        <Undo2 className="h-3 w-3" />
      ) : (
        <Trash2 className="h-3 w-3" />
      )}
    </Button>
  );
}

function IconSelector({ isDefaultView, iconId, viewType, color = null, project }) {
  const iconCls = getViewStateIconByRecord({ iconId, isDefaultView, viewType }, project);

  // In view mode, show static icon
  return (
    <div
      className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0"
    >
      <i className={`${iconCls} text-base ${color ? '' : 'text-muted-foreground'}`} style={color ? { color } : undefined}></i>
    </div>
  )
}

export default function MegaMenuContent({ onEditModeChange, onMenuItemClick }) {
  const history = useHistory();
  const { project, viewManager, user, selectedBoard, entityManager, editorHandler, boardManager } = useContext(BryntumContext);
  const {
    isEditMode,
    setIsEditMode,
    columns,
    items,
    draggedItem,
    dragOverItem,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleSave,
    handleCancel,
    updateColumnName,
    updateMenuItem,
    setDragOverItem,
    setItemPendingDelete,
  } = useViewsMenu()

  const isAdmin = user && user.can(GRANTS.grant_one);
  const [showArchive, setShowArchive] = React.useState(getIsArchivedForBoard(selectedBoard?.record?.slug));

  // Keep showArchive in sync with board changes
  useEffect(() => {
    setShowArchive(getIsArchivedForBoard(selectedBoard?.record?.slug));
  }, [selectedBoard?.record?.slug]);

  const handleSettingsClick = () => {
    const id = StringHelper.generateUUID();
    const settingsUrl = `/${selectedBoard.record.slug}/settings`;
    history.push(settingsUrl, { viewId: id, viewType: VIEW_TYPES.settings });
    viewManager.selectViewAnonymousView(id, "Settings", VIEW_TYPES.settings);
    onMenuItemClick();
    if (entityManager.isOpen) {
      entityManager.closeEntityManager();
    }
  };

  const handleBoardContextLayerClick = useCallback((viewName = "Context") => {
    if (isEditMode) return;
    viewManager.openAnonymousViewByType({
      viewType: VIEW_TYPES.settings,
      viewName,
      otherParams: { setting: SETTING_KEYS.BOARD_CONTEXT },
    });
    onMenuItemClick();
    if (entityManager.isOpen) {
      entityManager.closeEntityManager();
    }
  }, [isEditMode, viewManager, onMenuItemClick, entityManager]);

  const handleMySwivuClick = () => {
    const id = StringHelper.generateUUID();
    history.push(`/${selectedBoard.record.slug}/${VIEW_TYPES.mySwivu}`, { viewId: id, viewType: VIEW_TYPES.mySwivu });
    viewManager.selectViewAnonymousView(id, "My Swivu", VIEW_TYPES.mySwivu);
    onMenuItemClick();
  };

  const handleShowArchiveChange = (checked) => {
    setShowArchive(checked);
    const selectedBoardSlug = selectedBoard?.record?.slug;
    if (selectedBoardSlug) {
      saveIsArchivedForBoard(selectedBoardSlug, checked);
      setTimeout(() => boardManager.reloadSelectedBoard(), 0);
    }
  };

  const handleMenuItemClickInternal = useCallback((item) => {
    if (isEditMode) return;
    const view = viewManager.getViewById(item.id);
    if (view.viewType === VIEW_TYPES.context) {
      handleBoardContextLayerClick(view.name);
      return;
    }
    viewManager.selectView(view);
    onMenuItemClick();
    if (entityManager.isOpen) {
      entityManager.closeEntityManager();
    }
  }, [isEditMode, entityManager, viewManager, onMenuItemClick, handleBoardContextLayerClick]);

  const canDeleteView = useCallback((itemId) => {
    const view = project?.viewsStore?.getById(itemId);
    return view?.canBeDeletedBy(user) ?? false;
  }, [project, user]);

  const canShowDeleteControl = useCallback((item) => (
    canDeleteView(item.id) || item.isPendingDelete
  ), [canDeleteView]);

  const handleDeleteView = useCallback((e, item) => {
    e.stopPropagation();
    setItemPendingDelete(item.id, !item.isPendingDelete && canDeleteView(item.id));
  }, [canDeleteView, setItemPendingDelete]);

  const renderMenuItem = useCallback(
    (item, columnId) => {
      const getItemDescription = () => {
        if (!item.description) {
          return isEditMode ? "" : "No description available."
        }
        return item.description
      }

      return (
        <div
          key={item.id}
          draggable={isEditMode && !item.isPendingDelete}
          onClick={() => handleMenuItemClickInternal(item)}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move"
            e.dataTransfer.setData("text/plain", item.id)
            attachDragImage(e)
            handleDragStart(e, item.id, columnId, item.order)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleDragOver(e, columnId, item.order)
          }}
          onDragEnter={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleDragLeave()
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleDrop(e, columnId, item.order)
          }}
          onDragEnd={handleDragEnd}
          className={`group relative flex items-start rounded-lg transition-all duration-200 space-x-2 p-2 hover:bg-accent/50 ${
            isEditMode ? "cursor-move" : "cursor-pointer"
          } ${item.isPendingDelete ? "opacity-70" : ""} ${
            draggedItem?.itemId === item.id ? "opacity-50 bg-accent" : ""
          } ${
            dragOverItem?.columnId === columnId &&
            dragOverItem?.targetOrder === item.order &&
            draggedItem?.itemId !== item.id
              ? "border-t-2 border-primary"
              : ""
          } ${!isEditMode ? "hover:scale-[1.02] active:scale-[0.98]" : ""}`}
        >
          <div className="flex items-center space-x-2 flex-shrink-0">
            {isEditMode && !item.isPendingDelete && (
              <GripVertical className="w-4 h-4 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0" />
            )}
            <IconSelector
              iconId={item.iconId}
              color={item.color}
              project={project}
              isDefaultView={item.isDefaultView}
              viewType={item.viewType}
            />
          </div>
          <div className={`flex-1 min-w-0 relative overflow-hidden ${
            isEditMode && canShowDeleteControl(item) ? "pr-5" : ""
          }`}>
            <div className="flex items-center space-x-2 min-h-5">
              <div className="flex-1 min-w-0">
                <EditableText
                  id={`${item.id}-title`}
                  value={item.name}
                  onChange={(value) => updateMenuItem(item.id, "name", value)}
                  className="font-medium text-foreground text-sm leading-5 block truncate"
                  isEditMode={isEditMode && !item.isPendingDelete}
                />
              </div>
              {item.count && !isEditMode ? (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full ml-2 flex-shrink-0">
                  {item.count}
                </span>
              ) : null}
            </div>
            <div className="min-h-4 mt-1">
              <div className="flex-1 min-w-0">
                {isEditMode && item.isPendingDelete ? (
                  <PendingDeleteLabel />
                ) : (
                  <EditableText
                    id={`${item.id}-description`}
                    value={getItemDescription()}
                    onChange={(value) => updateMenuItem(item.id, "description", value)}
                    className={`${!isEditMode ? "text-muted-foreground" : "text-foreground/80"} text-xs leading-4 block truncate`}
                    isEditMode={isEditMode && !item.isPendingDelete}
                  />
                )}
              </div>
            </div>
          </div>
          {isEditMode && canShowDeleteControl(item) && (
            <FloatingDeleteButton
              isPendingDelete={item.isPendingDelete}
              onClick={(e) => handleDeleteView(e, item)}
            />
          )}
        </div>
      )
    },
    [
      isEditMode,
      draggedItem,
      dragOverItem,
      handleDragStart,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleDragEnd,
      updateMenuItem,
      handleMenuItemClickInternal,
      canDeleteView,
      canShowDeleteControl,
      handleDeleteView,
      project,
    ],
  )

  // Helper function to get items for a column, sorted by order and filtered by hidden status
  const getColumnItems = useCallback((columnId) => {
    return items
      .filter((item) => !item.isHidden && item.columnId === columnId)
      .sort((a, b) => a.order - b.order)
  }, [items])

  const getHiddenItems = useCallback(() => {
    return items.filter((item) => item.isHidden);
  }, [items])

  const renderHiddenItemPill = useCallback(
    (item) => (
        <div
          key={item.id}
          draggable={!item.isPendingDelete}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move"
            e.dataTransfer.setData("text/plain", item.id)
            attachDragImage(e)
            handleDragStart(e, item.id, "hidden", item.order)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleDragOver(e, "hidden", item.order, true)
          }}
          onDragEnter={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleDragLeave()
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleDrop(e, "hidden", item.order, true)
          }}
          onDragEnd={handleDragEnd}
          className={`group relative inline-flex items-center gap-2 bg-background rounded-lg px-3 py-2 border transition-all duration-200 ${
            item.isPendingDelete
              ? "opacity-70 cursor-default"
              : "cursor-move hover:shadow-sm hover:border-primary/30 hover:scale-105"
          } ${
            draggedItem?.itemId === item.id ? "opacity-50 bg-accent border-border scale-95" : ""
          } ${
            dragOverItem?.columnId === "hidden" &&
            dragOverItem?.targetOrder === item.order &&
            draggedItem?.itemId !== item.id
              ? "ring-2 ring-primary ring-offset-1"
              : ""
          }`}
          title={`${item.name}${item.description ? ` - ${item.description}` : ""}`}
        >
          <IconSelector
            iconId={item.iconId}
            color={item.color}
            project={project}
            isDefaultView={item.isDefaultView}
            viewType={item.viewType}
          />
          <div className={`flex flex-col min-w-0 gap-0.5 ${
            canShowDeleteControl(item) ? "pr-5" : ""
          }`}>
            <span className="text-sm font-medium text-foreground truncate">
              {item.name}
            </span>
            {item.isPendingDelete ? (
              <PendingDeleteLabel className="text-[10px] text-destructive/80 leading-3" />
            ) : null}
          </div>
          {canShowDeleteControl(item) && (
            <FloatingDeleteButton
              isPendingDelete={item.isPendingDelete}
              onClick={(e) => handleDeleteView(e, item)}
            />
          )}
        </div>
    ),
    [draggedItem, dragOverItem, handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd, canShowDeleteControl, handleDeleteView, project],
  )

  const renderHiddenSectionContent = useCallback(() => {
    const hiddenItems = getHiddenItems();
    if (hiddenItems.length > 0) {
      return (
        <div className="flex flex-wrap gap-2">
          {hiddenItems.map((item) => renderHiddenItemPill(item))}

          {/* Drop zone indicator for reordering within hidden items */}
          {draggedItem?.isFromHidden && (
            <div
              className={`inline-flex items-center justify-center w-16 h-10 rounded-full border-2 border-dashed transition-all duration-200 ${
                dragOverItem?.isHiddenSection && dragOverItem?.targetOrder === hiddenItems.length
                  ? "border-primary bg-accent"
                  : "border-border bg-muted"
              }`}
              onDragOver={(e) => {
                e.preventDefault()
                e.stopPropagation()
                e.dataTransfer.dropEffect = "move"
                setDragOverItem({
                  columnId: "hidden",
                  targetOrder: hiddenItems.length,
                  isHiddenSection: true,
                })
              }}
              onDragEnter={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDragLeave()
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDrop(e, "hidden", hiddenItems.length, true)
              }}
            >
              <div className="w-2 h-2 rounded-full bg-muted-foreground" />
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="text-center text-muted-foreground py-2">
        <EyeOff className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p className="text-sm font-medium">No hidden items</p>
        <p className="text-xs">Drag items here to hide them</p>
      </div>
    )
  }, [getHiddenItems, draggedItem, dragOverItem, renderHiddenItemPill, handleDragLeave, handleDrop, setDragOverItem])

  // Notify parent component when edit mode changes
  useEffect(() => {
    onEditModeChange?.(isEditMode)
  }, [isEditMode, onEditModeChange])

  return (
    <div className="h-full flex flex-col bg-background transition-all duration-300 rounded-lg overflow-hidden">
      {/* Scrollable Menu Sections */}
      <div
        className="flex-1 overflow-y-auto p-8"
      >
        <div className="flex flex-row gap-6">
          {columns.map((column) => {
            const columnItems = getColumnItems(column.id) // Only show non-hidden items
            const hasVisibleItems = columnItems.length > 0;
            const canEdit = isEditMode && isAdmin;
            return (
              <div key={column.id} className="space-y-4 flex flex-col flex-1 min-w-0">
                <EditableText
                  id={`${column.id}-name`}
                  value={column.name}
                  onChange={(value) => updateColumnName(column.id, value)}
                  className={`${!canEdit ? "uppercase text-muted-foreground" : "text-foreground"} text-xs font-semibold tracking-wider`}
                  isEditMode={canEdit}
                />
                <div
                  className={`flex flex-col justify-start gap-y-1 flex-1 ${
                    isEditMode && !hasVisibleItems && dragOverItem?.columnId === column.id
                      ? "border-2 border-dashed border-primary bg-accent rounded-lg p-4"
                      : ""
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isEditMode && !hasVisibleItems) {
                      e.dataTransfer.dropEffect = "move"
                      setDragOverItem({ columnId: column.id, targetOrder: 0 })
                    }
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleDragLeave()
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isEditMode && !hasVisibleItems) {
                      handleDrop(e, column.id, 0)
                    }
                  }}
                >
                  {!hasVisibleItems ? (
                    <div className="flex flex-col h-full justify-start items-center gap-1 p-2 pointer-events-none">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted mb-2">
                        <i className="fa-light fa-folder-open text-muted-foreground" />
                      </div>
                      <h4 className="text-sm font-medium text-foreground mb-1 mt-0 text-center">
                        No Views In This Column
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed max-w-xs text-center">
                        This column has no views. Add some to organize your workspace.
                      </p>
                    </div>
                  ) : (
                    columnItems.map((item) => renderMenuItem(item, column.id))
                  )}

                  {/* Drop zone for appending items at the bottom of a non-empty column */}
                  {isEditMode && columnItems.length > 0 && (
                    <div
                      className={`h-full min-h-12 w-full rounded-lg ${
                        dragOverItem?.columnId === column.id && dragOverItem?.targetOrder === columnItems.length
                        ? "border-2 border-dashed border-primary bg-accent"
                          : ""
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        e.dataTransfer.dropEffect = "move"
                        setDragOverItem({ columnId: column.id, targetOrder: columnItems.length })
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleDragLeave()
                      }}
                      onDrop={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleDrop(e, column.id, columnItems.length)
                      }}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>


      {/* Dynamic Hidden Items Section - Only show in edit mode */}
      {isEditMode && (
        <div className="sticky bottom-0 border-t border-border px-8 py-4 z-10 bg-background/95 backdrop-blur-sm">
          <div className="mb-3">
            <div className="flex items-center space-x-2 mb-2">
              <EyeOff className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground tracking-wide">Hidden Items</span>
              <span className="text-xs text-muted-foreground">
                ({getHiddenItems().length} item
                {getHiddenItems().length !== 1 ? "s" : ""})
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {getHiddenItems().length > 0
                ? "Drag items here to hide them from the main view. Drag hidden items back to columns to restore them."
                : "Drag items here to hide them from the main view."}
            </p>
          </div>
          <div
            className={`min-h-20 max-h-36 overflow-y-auto p-4 border-2 border-dashed border-border rounded-lg bg-muted transition-all duration-200 ${
              dragOverItem?.isHiddenSection ? "border-primary bg-primary/10" : ""
            }`}
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
              e.dataTransfer.dropEffect = "move"
              setDragOverItem({
                columnId: "hidden",
                targetOrder: getHiddenItems().length,
                isHiddenSection: true,
              })
            }}
            onDragEnter={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleDragLeave()
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleDrop(e, "hidden", getHiddenItems().length, true)
            }}
          >
            {renderHiddenSectionContent()}
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="sticky bottom-0 z-20 bg-background/95 backdrop-blur-sm rounded-b-lg flex-shrink-0 border-t border-border/50">
        <div className="px-8 py-4">
          {isEditMode ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground font-medium">Editing view layout</span>
              <div className="flex gap-2">
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  size="sm"
                  className="text-sm"
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="text-sm"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  Save Changes
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-6">
              {/* Left: Navigation & Settings */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleMySwivuClick}
                  variant="ghost"
                  size="sm"
                  className="font-medium text-sm"
                >
                  <Home className="h-4 w-4" />
                  My Swivu
                </Button>

                <Button
                  onClick={handleSettingsClick}
                  variant="ghost"
                  size="sm"
                  className="font-medium text-sm"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </Button>

                <Button
                  onClick={() => {
                    editorHandler.openEditor(EDITOR_KEYS.myMediaEditor, null, {}, { by: "recordType", useExisting: true });
                    onMenuItemClick();
                  }}
                  variant="ghost"
                  size="sm"
                  className="font-medium text-sm"
                >
                  <Library className="h-4 w-4" />
                  My Media
                </Button>

                <Button
                  onClick={() => {
                    const agendaUrl = createAgendaUrl();
                    window.open(`${agendaUrl}/hq`, "_blank");
                    onMenuItemClick();
                  }}
                  variant="ghost"
                  size="sm"
                  className="font-medium text-sm"
                >
                  <i className="fa-solid fa-arrow-up-right" />
                  My Workforce Assignments
                </Button>
              </div>

              {/* Right: Show Archive, Theme & Edit */}
              <div className="flex items-center gap-3">
                {/* Show Archive Checkbox */}
                {selectedBoard && user && user.can(GRANTS.grant_four) && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show-archive"
                      checked={showArchive}
                      onCheckedChange={handleShowArchiveChange}
                    />
                    <label
                      htmlFor="show-archive"
                      className="font-medium leading-none cursor-pointer text-sm"
                    >
                      Show Archive
                    </label>
                  </div>
                )}

                <ThemeSwitcher />

                <Button
                  onClick={() => setIsEditMode(true)}
                  size="sm"
                  variant="ghost"
                  className="px-2"
                  title="Edit Layout"
                >
                  <Edit3 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
